/**
 * Metadata field patterns for automatic column classification
 * Used across the study data import components for consistent mapping
 */

export interface MetadataTypeDefinition {
  type: string;
  label: string;
  description: string;
  priority: number;
  patterns: (RegExp | string)[];
}

export const METADATA_PATTERNS: MetadataTypeDefinition[] = [
  // REQUIRED metadata with high priority
  {
    type: 'externalSubjectId',
    label: 'External Subject ID',
    description: 'Required: External patient reference',
    priority: 100,
    patterns: [
      /^subjectid$/i,
      /^external_subject_id$/i,
      /^externalSubjectId$/i,
      /^subject_id$/i,
      /^subj_id$/i,
      /^ext_subj_id$/i,
      /^subj_id$/i,
      /^ext_subj_id$/i,
      /^external_id$/i,
      /^externalid$/i,
    ],
  },
  {
    type: 'subjectNumber',
    label: 'Subject Number',
    description: 'Required: Internal subject numbering',
    priority: 100,
    patterns: [
      /^subjectnumber$/i,
      /^subject_number$/i,
      /^subj_number$/i,
      /^subjectnum$/i,
      /^subj_num$/i,
      /^subject_no$/i,
      /^subj_no$/i,
    ],
  },
  {
    type: 'siteNumber',
    label: 'Site Number',
    description: 'Required: Numeric site identifier',
    priority: 100,
    patterns: [
      /^sitenumber$/i,
      /^site_number$/i,
      /^site_num$/i,
      /^site_no$/i,
      /^sitenum$/i,
      /^siteid$/i,
      /^site_id$/i,
    ],
  },
  {
    type: 'formId',
    label: 'Form ID',
    description: 'Required: Form identifier',
    priority: 100,
    patterns: [/^formid$/i, /^form_id$/i, /^formid$/i, /^frm_id$/i],
  },
  {
    type: 'mainFormId',
    label: 'Main Form ID',
    description: 'Required: Parent form identifier',
    priority: 100,
    patterns: [
      /^mainformid$/i,
      /^main_form_id$/i,
      /^parent_form_id$/i,
      /^mainform$/i,
      /^main_form$/i,
    ],
  },
  // Other metadata types
  {
    type: 'siteName',
    label: 'Site Name',
    description: 'Human-readable site name',
    priority: 80,
    patterns: [
      /^sitename$/i,
      /^site_name$/i,
      /^site$/i,
      /^site_desc$/i,
      /^site_description$/i,
    ],
  },
  {
    type: 'subjectData',
    label: 'Subject Data',
    description: 'General subject information',
    priority: 60,
    patterns: [/^subject$/i, /^patient$/i, /^subj$/i, /^pt$/i],
  },
  {
    type: 'deletedForm',
    label: 'Deleted Form',
    description: 'Deletion flag',
    priority: 50,
    patterns: [
      /^deleted$/i,
      /^is_deleted$/i,
      /^deleted_flag$/i,
      /^active$/i,
      /^is_active$/i,
    ],
  },
  {
    type: 'formIndex',
    label: 'Form Index',
    description: 'Form ordering',
    priority: 50,
    patterns: [
      /^formindex$/i,
      /^form_index$/i,
      /^index$/i,
      /^order$/i,
      /^sequence$/i,
      /^seq$/i,
    ],
  },
  {
    type: 'site',
    label: 'Site',
    description: 'Site identification',
    priority: 40,
    patterns: [/^site$/i, /^site_code$/i],
  },
  {
    type: 'trash',
    label: 'Trash',
    description: 'Deleted form flag',
    priority: 30,
    patterns: [
      /^trash$/i,
      /^deleted$/i,
      /^is_deleted$/i,
      /^deleted_flag$/i,
      /^active$/i,
      /^is_active$/i,
    ],
  },
  {
    type: 'isPatientPregnant',
    label: 'Is Patient Pregnant',
    description: 'Patient pregnancy status',
    priority: 70,
    patterns: [
      /^is_patient_pregnant$/i,
      /^patient_pregnant$/i,
      /^pregnant$/i,
      /^is_pregnant$/i,
    ],
  },
  {
    type: 'isPatientLactating',
    label: 'Is Patient Lactating',
    description: 'Patient lactation status',
    priority: 70,
    patterns: [
      /^is_patient_lactating$/i,
      /^patient_lactating$/i,
      /^lactating$/i,
      /^is_lactating$/i,
    ],
  },
] as const;

/**
 * Get metadata type definition by type name
 */
export const getMetadataTypeDefinition = (
  type: string
): MetadataTypeDefinition | undefined => {
  return METADATA_PATTERNS.find(metaType => metaType.type === type);
};

/**
 * Get all required metadata types
 */
export const getRequiredMetadataTypes = (): MetadataTypeDefinition[] => {
  return METADATA_PATTERNS.filter(metaType => metaType.priority === 100);
};

/**
 * Check if a metadata type is required
 */
export const isRequiredMetadataType = (type: string): boolean => {
  const definition = getMetadataTypeDefinition(type);
  return definition ? definition.priority === 100 : false;
};
